// dependencies
import { BarElement, CategoryScale, Chart as ChartJS, LinearScale } from "chart.js";
import { differenceInCalendarMonths, startOfMonth } from "date-fns";
import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";

import { getColor } from "../../../../../utils/getColor";

ChartJS.register(CategoryScale, LinearScale, BarElement);

interface BarchartProps {
	monthlyData: { time_bucket: Date; average: number }[];
}

const Barchart = (props: BarchartProps) => {
	const { monthlyData } = props;

	const barchartMonthlyData: number[] = useMemo(() => {
		// object with 12 keys, using indexes 11 - 0 as keys to represent the number of months ago
		const barchartData = Object.fromEntries([...Array(12)].map((_, i) => [i, 0]));
		// now loop through the monthlyData and add the average to the barchartData object
		if (monthlyData.length) {
			monthlyData.forEach((summary) => {
				const monthsAgo = differenceInCalendarMonths(
					startOfMonth(new Date()),
					startOfMonth(summary.time_bucket)
				);
				if (monthsAgo < 12) {
					// if the month already has data, average it with the new data
					if (barchartData[monthsAgo.toString()] > 0) {
						// new average = current average + this average / 2
						barchartData[monthsAgo.toString()] = (barchartData[monthsAgo] + summary.average) / 2;
					}
					// otherwise just add the new data, replacing the 0
					else {
						barchartData[monthsAgo.toString()] = summary.average;
					}
				}
			});
		}

		// returns data at indexes 11 - 0 of the monthlyData object
		// with indexes representing the number of moths ago
		return [...Array(12)].map((_, i) => {
			return barchartData[(11 - i).toString()];
		});
	}, [monthlyData]);

	return (
		<Bar
			options={{
				responsive: true,
				maintainAspectRatio: false,
				elements: {
					bar: {
						borderWidth: 0,
						borderRadius: 2,
					},
				},
				plugins: {
					legend: {
						display: false,
					},
					tooltip: {
						enabled: false,
					},
				},
				hover: { mode: undefined },
				scales: {
					x: {
						display: false,
						stacked: true,
					},
					y: {
						max: 1,
						display: false,
					},
				},
			}}
			data={{
				labels: Array(12).fill(""),
				datasets: [
					{
						label: "Bar Backgrounds",
						data: Array(12).fill(5),
						backgroundColor: getColor("neutral", 2),
						barPercentage: 0.83,
						// order means order that the bar is drawn
						order: 1,
					},
					{
						label: "Average Score",
						data: barchartMonthlyData,
						backgroundColor: getColor("neutral", 5),
						barPercentage: 0.83,
						// this one is drawn last so ends up on top
						order: 0,
					},
				],
			}}
		/>
	);
};

export default Barchart;
